import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Radio, Input, Row, Col, DatePicker, Alert, Select, Form } from '../';
import { useGetMoveGroupsQuery, useGetUsersQuery } from '../../api';
import './MoveGroupSelector.scss';

const MoveGroupSelector = ({
    value,
    onChange,
    userOrg,
    className = '',
    style = {},
    disabled = false,
    hideCreateNew = false,
}) => {
    const [mode, setMode] = useState('existing'); // 'existing' or 'new'
    const [selectedMoveGroupId, setSelectedMoveGroupId] = useState('');
    const [newMoveGroupName, setNewMoveGroupName] = useState('');
    const [targetMigrationDate, setTargetMigrationDate] = useState(null);
    const [assignedResourceId, setAssignedResourceId] = useState('');

    const { data: moveGroupsData, isLoading: isLoadingMoveGroups } = useGetMoveGroupsQuery(userOrg);
    const { data: usersData, isLoading: isLoadingUsers } = useGetUsersQuery(userOrg);

    const moveGroups = moveGroupsData?.content || [];
    const users = usersData?.content || [];

    // Create options array for move groups select
    const moveGroupOptions = useMemo(
        () =>
            moveGroups.map((moveGroup) => ({
                value: moveGroup.id,
                label: moveGroup.name,
            })),
        [moveGroups],
    );

    // Initialize value from props
    useEffect(() => {
        if (value) {
            if (value.mode) {
                setMode(value.mode);
            }
            if (value.selectedMoveGroupId) {
                setSelectedMoveGroupId(value.selectedMoveGroupId);
            }
            if (value.newMoveGroupName) {
                setNewMoveGroupName(value.newMoveGroupName);
            }
            if (value.targetMigrationDate) {
                setTargetMigrationDate(value.targetMigrationDate);
            }
            if (value.assignedResourceId) {
                setAssignedResourceId(value.assignedResourceId);
            }
        }
    }, [value]);

    const handleModeChange = useCallback(
        (e) => {
            const newMode = e.target.value;
            setMode(newMode);

            // Reset values when mode changes
            if (newMode === 'existing') {
                setNewMoveGroupName('');
                setTargetMigrationDate(null);
                setAssignedResourceId('');
            } else {
                setSelectedMoveGroupId('');
            }

            updateValue({
                mode: newMode,
                selectedMoveGroupId: newMode === 'existing' ? selectedMoveGroupId : '',
                newMoveGroupName: newMode === 'new' ? newMoveGroupName : '',
                targetMigrationDate: newMode === 'new' ? targetMigrationDate : null,
                assignedResourceId: newMode === 'new' ? assignedResourceId : '',
            });
        },
        [selectedMoveGroupId, newMoveGroupName, targetMigrationDate, assignedResourceId],
    );

    const updateValue = useCallback(
        (newValue) => {
            onChange?.(newValue);
        },
        [onChange],
    );

    const handleMoveGroupSelect = useCallback(
        (moveGroupId) => {
            setSelectedMoveGroupId(moveGroupId);
            updateValue({
                mode,
                selectedMoveGroupId: moveGroupId,
                newMoveGroupName,
                targetMigrationDate,
                assignedResourceId,
            });
        },
        [mode, newMoveGroupName, targetMigrationDate, assignedResourceId, updateValue],
    );

    const handleNewMoveGroupNameChange = useCallback(
        (e) => {
            const name = e.target.value;
            setNewMoveGroupName(name);
            updateValue({
                mode,
                selectedMoveGroupId,
                newMoveGroupName: name,
                targetMigrationDate,
                assignedResourceId,
            });
        },
        [mode, selectedMoveGroupId, targetMigrationDate, assignedResourceId, updateValue],
    );

    const handleTargetDateChange = useCallback(
        (date) => {
            setTargetMigrationDate(date);
            updateValue({
                mode,
                selectedMoveGroupId,
                newMoveGroupName,
                targetMigrationDate: date,
                assignedResourceId,
            });
        },
        [mode, selectedMoveGroupId, newMoveGroupName, assignedResourceId, updateValue],
    );

    const handleAssignedResourceChange = useCallback(
        (resourceId) => {
            setAssignedResourceId(resourceId);
            updateValue({
                mode,
                selectedMoveGroupId,
                newMoveGroupName,
                targetMigrationDate,
                assignedResourceId: resourceId,
            });
        },
        [mode, selectedMoveGroupId, newMoveGroupName, targetMigrationDate, updateValue],
    );

    const getMoveGroupName = useCallback(
        (moveGroupId) => {
            if (!moveGroups) return '';
            const moveGroup = moveGroups.find((mg) => mg.id === moveGroupId);
            return moveGroup ? moveGroup.name : '';
        },
        [moveGroups],
    );

    const hasNoMoveGroups = moveGroups.length === 0;

    return (
        <div className={`move-group-selector ${className}`} style={style}>
            {!hideCreateNew && (
                <div className="mode-selector">
                    <Radio.Group value={mode} onChange={handleModeChange} disabled={disabled}>
                        <Radio value="new">Create new</Radio>
                        <Radio value="existing">Use existing</Radio>
                    </Radio.Group>
                </div>
            )}

            {hasNoMoveGroups && mode === 'existing' && !hideCreateNew && (
                <Alert
                    title="No Move Groups available. If you would like to create a new one, please select 'Create new'."
                    type="info"
                    showIcon
                    style={{ marginTop: 8, marginBottom: 8 }}
                />
            )}

            {mode === 'existing' && !hasNoMoveGroups && (
                <Select
                    placeholder="Select a Move Group"
                    value={selectedMoveGroupId || undefined}
                    onChange={handleMoveGroupSelect}
                    options={moveGroupOptions}
                    loading={isLoadingMoveGroups}
                    allowClear
                    disabled={disabled}
                    showSearch={{
                        filterOption: (input, option) =>
                            option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false,
                    }}
                    style={{ width: '100%', marginTop: hideCreateNew ? 0 : 8 }}
                />
            )}

            {mode === 'new' && (
                <div className="new-move-group-form" style={{ marginTop: hideCreateNew ? 0 : 8 }}>
                    <Form.Item
                        name="new_move_group_name"
                        rules={[
                            { required: true, message: 'Please enter a Move Group name' },
                            { whitespace: true, message: 'Move Group name cannot be empty' },
                        ]}
                        style={{ marginBottom: 8 }}
                    >
                        <Input
                            placeholder="Enter new Move Group name (required)"
                            value={newMoveGroupName}
                            onChange={handleNewMoveGroupNameChange}
                            disabled={disabled}
                        />
                    </Form.Item>

                    <Row gutter={16} style={{ marginBottom: 8 }}>
                        <Col span={12}>
                            <label>Target Migration Date</label>
                            <DatePicker
                                value={targetMigrationDate}
                                onChange={handleTargetDateChange}
                                disabled={disabled}
                                style={{ width: '100%' }}
                                placeholder="Select date"
                            />
                        </Col>
                        <Col span={12}>
                            <label>Assigned Resource</label>
                            <Select
                                placeholder="Select a User"
                                value={assignedResourceId || undefined}
                                onChange={handleAssignedResourceChange}
                                loading={isLoadingUsers}
                                allowClear
                                disabled={disabled}
                                style={{ width: '100%' }}
                                options={users.map((user) => ({ key: user.id, value: user.email, label: user.email }))}
                            />
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default MoveGroupSelector;
